import { useState } from "react";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/constants";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, ArrowUpRight, ShieldCheck } from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_draft-preview-11/artifacts/51ooxmc2_IMG-20241113-WA0005.webp";

// NSW state outline — accurate silhouette (matches user-supplied outline reference)
const NSWIcon = ({ size = 14 }) => (
  <svg
    width={size * 1.35}
    height={size}
    viewBox="0 0 108 80"
    fill="none"
    stroke="currentColor"
    strokeWidth="6"
    strokeLinejoin="round"
    strokeLinecap="round"
    aria-hidden="true"
    className="shrink-0"
  >
    <path d="M6 18 L92 14 L102 22 L104 40 L96 50 L86 58 L74 56 L64 62 L58 70 L48 74 L34 72 L26 66 L14 68 L8 60 L4 42 Z" />
  </svg>
);

// NATA accreditation badge — hexagon outline with "NATA" wordmark (matches user-supplied logo)
const NATAIcon = ({ size = 14 }) => (
  <svg
    width={size * 1.25}
    height={size}
    viewBox="0 0 40 32"
    fill="none"
    aria-hidden="true"
    className="shrink-0"
  >
    <path
      d="M20 2 L37 10 L37 22 L20 30 L3 22 L3 10 Z"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
    <text
      x="20"
      y="20"
      textAnchor="middle"
      fontFamily="ui-sans-serif, system-ui, sans-serif"
      fontWeight="900"
      fontSize="9"
      fill="currentColor"
      letterSpacing="0.4"
    >
      NATA
    </text>
  </svg>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\n\n${form.message}`
    );
    const subject = encodeURIComponent(form.subject || "Website enquiry");
    window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const contactRows = [
    {
      icon: Phone,
      label: "24/7 Response",
      value: COMPANY.phoneAlt,
      href: `tel:${COMPANY.phoneAltTel}`,
      testid: "contact-row-phone-emergency",
    },
    {
      icon: Phone,
      label: "Personal Line",
      value: COMPANY.phone,
      href: `tel:${COMPANY.phoneTel}`,
      testid: "contact-row-phone-personal",
    },
    {
      icon: Mail,
      label: "Email",
      value: COMPANY.email,
      href: `mailto:${COMPANY.email}`,
      testid: "contact-row-email",
    },
    {
      icon: MapPin,
      label: "Postal",
      value: "PO Box 47, Narwee NSW 2209",
      href: "#",
      testid: "contact-row-address",
    },
    {
      icon: Clock,
      label: "Hours",
      value: "Mon–Fri  ·  7:00am – 5:00pm AEST",
      href: "#",
      testid: "contact-row-hours",
    },
  ];

  return (
    <div data-testid="contact-page" className="min-h-[calc(100vh-80px)] bg-[#0A1428]">
      <Seo
        title="Contact ISGS Asbestos Consulting"
        description="Contact ISGS Asbestos Consulting — phone 0405 215 940 or 1800 47 48 49, email admin@isgsasbestos.com.au. NSW-based with 24/7 emergency asbestos response."
        path="/contact"
      />

      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-80px)]">

        {/* ─────────── LEFT — FULL-BLEED IMAGE PANEL ─────────── */}
        <section
          data-testid="contact-hero-panel"
          className="relative overflow-hidden bg-[#0A1428] text-white lg:min-h-[calc(100vh-80px)]"
        >
          {/* Background image — earth-mound / soil remediation field shot */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
            aria-hidden
          />
          {/* Gradient overlay — lighter on the left so the field is visible, darker on the right where text sits */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1428]/25 via-[#0A1428]/55 to-[#0A1428]/85" aria-hidden />
          {/* Lime accent diagonal */}
          <div className="absolute -bottom-1/3 -left-1/3 w-[120%] h-[60%] bg-[#A6CD3C]/8 blur-[120px] pointer-events-none" aria-hidden />
          {/* Subtle grid texture */}
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
            aria-hidden
          />

          {/* Content — right-aligned so the ISGS-branded hardhat on the left of the image stays visible */}
          <div className="relative z-10 h-full flex flex-col justify-between px-8 md:px-14 lg:px-16 py-14 lg:py-20 text-right">
            {/* TOP — Eyebrow + tagline */}
            <div className="ml-auto max-w-lg w-full">
              <div className="inline-flex items-center gap-3 mb-8">
                <span className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#A6CD3C]">
                  Get In Touch
                </span>
                <span className="w-12 h-[2px] bg-[#A6CD3C]" />
              </div>

              <h1
                className="text-[78px] md:text-[112px] lg:text-[140px] leading-[1.02] tracking-[0.01em] mb-7"
                style={{ fontFamily: "'Fredoka', system-ui, sans-serif", fontWeight: 700 }}
              >
                LET'S<br />
                <span className="inline-flex items-baseline gap-3">
                  TALK
                  <span className="text-[#A6CD3C]">.</span>
                </span>
              </h1>

              <p className="text-white/70 text-[15px] md:text-[16px] max-w-md ml-auto leading-relaxed">
                Subject-matter experts for asbestos and hazmat — across NSW and Australia-wide.
                Reach out via the channel that suits you, or send a direct message.
              </p>
            </div>

            {/* MIDDLE — Contact rows (right-aligned, icon on the right) */}
            <div className="my-12 lg:my-16 space-y-1 ml-auto max-w-lg w-full" data-testid="contact-rows">
              {contactRows.map((r) => (
                <a
                  key={r.label}
                  href={r.href}
                  data-testid={r.testid}
                  className="group flex items-center gap-5 py-4 border-b border-white/10 hover:border-[#A6CD3C]/50 transition-colors"
                >
                  <ArrowUpRight
                    size={16}
                    className="text-white/30 group-hover:text-[#A6CD3C] group-hover:-translate-y-0.5 group-hover:-translate-x-0.5 transition-all flex-shrink-0 rotate-[-90deg] group-hover:rotate-[-90deg]"
                  />
                  <div className="flex-1 min-w-0 text-right">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 font-bold mb-0.5">
                      {r.label}
                    </div>
                    <div className="text-white text-[15px] md:text-[16px] font-semibold truncate">
                      {r.value}
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[#A6CD3C] border border-[#A6CD3C] flex items-center justify-center text-[#0E2A5C] group-hover:bg-white group-hover:border-white transition-all">
                    <r.icon size={16} />
                  </div>
                </a>
              ))}
            </div>

            {/* BOTTOM — Trust strip (right-aligned) */}
            <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-3">
              <div className="inline-flex items-center gap-2 text-white/70">
                <span className="text-[#A6CD3C]"><NSWIcon size={14} /></span>
                <span className="text-[12px] uppercase tracking-[0.22em] font-bold">
                  NSW · Australia-wide
                </span>
              </div>
              <div className="hidden md:block w-px h-4 bg-white/15" />
              <div className="inline-flex items-center gap-2 text-white/70">
                <span className="text-[#A6CD3C]"><NATAIcon size={14} /></span>
                <span className="text-[12px] uppercase tracking-[0.22em] font-bold">
                  NATA-Accredited Labs
                </span>
              </div>
              <div className="hidden md:block w-px h-4 bg-white/15" />
              <div className="inline-flex items-center gap-2 text-white/70">
                <ShieldCheck size={16} className="text-[#A6CD3C]" />
                <span className="text-[12px] uppercase tracking-[0.22em] font-bold">
                  18+ Years Experience
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────── RIGHT — FORM PANEL ─────────── */}
        <section
          data-testid="contact-form-panel"
          className="relative bg-[#F7F8F3] flex items-center"
        >
          {/* Decorative corner accents */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-[#A6CD3C]/10 blur-3xl rounded-full pointer-events-none" aria-hidden />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-[#3B6CA8]/10 blur-3xl rounded-full pointer-events-none" aria-hidden />

          <div className="relative z-10 w-full px-8 md:px-14 lg:px-16 py-14 lg:py-20">
            <div className="max-w-xl mx-auto">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-3 mb-6">
                <span className="w-10 h-[2px] bg-[#0A1428]" />
                <span className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#0A1428]">
                  Send a Message
                </span>
              </div>

              <h2 className="text-[34px] md:text-[42px] font-black text-[#0A1428] leading-[1.05] tracking-[-0.02em] mb-3">
                Tell us about your<br />
                <span className="text-[#3B6CA8]">site or situation.</span>
              </h2>
              <p className="text-[#5B6478] text-[14.5px] leading-relaxed mb-10 max-w-md">
                We typically respond within 24 hours. For urgent finds, call our 24/7 line.
              </p>

              {submitted ? (
                <div className="py-10" data-testid="contact-success">
                  <div className="w-14 h-14 rounded-full bg-[#A6CD3C]/15 flex items-center justify-center mb-5">
                    <CheckCircle2 size={28} className="text-[#A6CD3C]" />
                  </div>
                  <h3 className="text-[22px] font-bold text-[#0A1428] mb-3">Email client opened</h3>
                  <p className="text-[#5B6478] text-[14.5px] max-w-md leading-relaxed">
                    Your message has been prepared in your default email app. Hit send to reach us
                    — or call <strong className="text-[#0A1428]">{COMPANY.phoneAlt}</strong> for
                    immediate assistance.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <FloatField label="Full Name" name="name" value={form.name} onChange={onChange} required />
                    <FloatField label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <FloatField label="Phone" name="phone" value={form.phone} onChange={onChange} />
                    <FloatField label="Subject" name="subject" value={form.subject} onChange={onChange} />
                  </div>
                  <FloatField
                    label="Message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    required
                    multiline
                    rows={5}
                    placeholder="Tell us about your site, situation or query…"
                  />

                  <button
                    type="submit"
                    className="w-full bg-[#0A1428] hover:bg-[#A6CD3C] text-white hover:text-[#0A1428] py-5 px-6 rounded-xl font-black text-[13px] tracking-[0.22em] uppercase flex items-center justify-center gap-3 transition-all group border-2 border-[#0A1428] hover:border-[#A6CD3C] shadow-[0_15px_35px_-15px_rgba(10,20,40,0.4)]"
                    data-testid="contact-submit-btn"
                  >
                    Send Message
                    <Send size={15} className="group-hover:translate-x-1 transition-transform" />
                  </button>

                  <p className="text-[#5B6478]/80 text-[12px] text-center pt-2">
                    By submitting you agree we may contact you regarding your enquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ─────────── FloatField — bold uppercase label, soft input ─────────── */
function FloatField({
  label,
  name,
  type = "text",
  value,
  onChange,
  required,
  multiline = false,
  rows = 4,
  placeholder,
}) {
  const baseInput =
    "w-full bg-white border-2 border-[#E5E7DA] rounded-xl px-5 py-4 text-[15px] text-[#0A1428] focus:outline-none focus:border-[#A6CD3C] focus:bg-white transition-all placeholder:text-[#5B6478]/50";

  return (
    <div>
      <label
        htmlFor={`contact-${name}`}
        className="block text-[10.5px] uppercase tracking-[0.26em] text-[#0A1428] font-black mb-2.5"
      >
        {label}
        {required && <span className="text-[#A6CD3C]"> *</span>}
      </label>
      {multiline ? (
        <textarea
          id={`contact-${name}`}
          name={name}
          rows={rows}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          className={`${baseInput} resize-none`}
          data-testid={`contact-${name}-input`}
        />
      ) : (
        <input
          id={`contact-${name}`}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          placeholder={placeholder}
          className={baseInput}
          data-testid={`contact-${name}-input`}
        />
      )}
    </div>
  );
}
